import { ToastAction } from "@/components/ui/toast";
import { useNotifications } from "@/hooks/useNotifications";
import { useToast } from "@/hooks/useToast";
import { useThreadStore } from "@/store";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * StreamingNotification - Listens for stream completion events and shows notifications
 * Shows toast when background streams complete with navigation to completed thread
 * Also creates persistent notifications in the Notifications Dialog
 */
export function StreamingNotification() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { threads, getStreamForThread, currentThreadId } = useThreadStore();
  const { sendSystemNotification } = useNotifications();

  useEffect(() => {
    const handleStreamCompleted = async (event) => {
      const { threadId, messageId } = event.detail;

      if (!threadId) return;

      // UPDATED: Show notification for all background streams, including when on /chat/new
      // Only skip notification if the stream is in the currently active foreground thread
      const isCurrentThread = threadId === currentThreadId;
      const isOnNewChat = currentThreadId === null;

      // Show notification for:
      // 1. Background threads (different from current thread)
      // 2. Any thread when user is on /chat/new (currentThreadId is null)
      if (!isCurrentThread || isOnNewChat) {
        // Get thread name for notification
        const thread = threads.find((t) => t.id === threadId);
        const threadName = thread?.title || "Chat";

        // Check if thread still has active stream (might have been cancelled)
        const activeStream = getStreamForThread(threadId);
        if (activeStream) {
          // Stream still active, don't show notification
          return;
        }

        // Show immediate toast notification with navigation - 10 second duration for visibility
        toast({
          title: "Message Completed",
          description: `Streaming completed in "${threadName}"`,
          duration: 10000,
          action: (
            <ToastAction
              altText="View thread"
              onClick={() => {
                navigate(`/chat/${threadId}`);
              }}
              className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30 font-medium"
            >
              View
            </ToastAction>
          ),
        });

        // Also create persistent notification in Notifications Dialog
        sendSystemNotification(
          "AI Response Completed",
          `Your AI response in "${threadName}" has finished streaming.`,
          {
            threadId,
            messageId,
            threadName,
            action: "stream-completed",
          }
        );
      }
    };

    const handleStreamLimitReached = async (event) => {
      const { abortedThreadId } = event.detail;

      if (!abortedThreadId) return;

      // Get thread name for notification
      const thread = threads.find((t) => t.id === abortedThreadId);
      const threadName = thread?.title || "Chat";

      // Show notification that a background stream was stopped
      toast({
        title: "Background Stream Paused",
        description: `Paused streaming in "${threadName}" due to concurrent stream limit.`,
        duration: 6000,
        variant: "default",
      });
    };

    // Listen for stream completion events
    window.addEventListener("stream-completed", handleStreamCompleted);
    window.addEventListener("stream-limit-reached", handleStreamLimitReached);

    return () => {
      window.removeEventListener("stream-completed", handleStreamCompleted);
      window.removeEventListener(
        "stream-limit-reached",
        handleStreamLimitReached
      );
    };
  }, [toast, navigate, threads, getStreamForThread, currentThreadId, sendSystemNotification]);

  // This component doesn't render anything
  return null;
}

export default StreamingNotification;
